import { motion } from "framer-motion";
import ParticleHero from "../landing/ParticleHero";
import { HEADSHOT_URL, NC, scrollToId } from "../landing/shared";
import { REPLAY_VIDEO_URL, APPLY_CTA_LABEL } from "./config";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
});

export default function ReplayHero() {
  return (
    <section id="watch" className="relative min-h-screen bg-grid-80 overflow-hidden" data-testid="replay-hero-section">
      <div className="absolute inset-0 opacity-50">
        <ParticleHero />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#001736] via-[#001736]/75 to-[#001736]/40 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-32 pb-16 grid lg:grid-cols-[45fr_55fr] gap-12 items-center">
        <div>
          <p className="font-mono-strict text-[10px] tracking-[0.28em] text-[#0162CA] mb-6">
            YOUR REPLAY IS READY — WATCH BEFORE IT EXPIRES
          </p>
          <h1
            className="font-black uppercase leading-[0.95]"
            style={{ fontSize: "clamp(36px, 5vw, 68px)", letterSpacing: "-0.03em" }}
            data-testid="replay-hero-headline"
          >
            <span className="block overflow-hidden">
              <motion.span className="block" {...lineAnim(0)}>A VA HOME PURCHASE</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block text-[#0162CA]" {...lineAnim(1)}>IS A WHOLE DEAL—</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block opacity-60" style={{ fontSize: "62%" }} {...lineAnim(2)}>
                NOT JUST A RATE QUOTE
              </motion.span>
            </span>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="mt-7 text-[15px] text-white/55 leading-relaxed max-w-xl">
              Watch the full VA Better-Buy Masterclass™ below. When you're ready for options built around your
              actual situation, complete a mortgage application and the required authorizations to request your
              one-on-one Better Deal Review™ with Josh.
            </p>
            <blockquote className="mt-6 border-l-2 border-[#0162CA] pl-4 italic text-sm text-white/60">
              Better means the whole deal makes sense.
            </blockquote>
            <p className="mt-6 font-mono-strict text-[11px] tracking-[0.15em] text-[#0162CA]" data-testid="replay-window-line">
              ⏳ This replay is available for <NC label="REPLAY_WINDOW" /> from your registration.
            </p>
            <div className="mt-8 flex items-center gap-4" data-testid="replay-host-module">
              <img src={HEADSHOT_URL} alt="Josh Bartlett" className="h-12 w-12 rounded-full object-cover border-2 border-[#0162CA]" />
              <div>
                <p className="text-sm font-bold">Josh Bartlett</p>
                <p className="text-xs text-white/45">Branch Manager · Shoreline Mortgage Group · NMLS# 1425493</p>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          {REPLAY_VIDEO_URL ? (
            <div className="relative aspect-video border border-white/12 bg-black" data-testid="replay-video-player">
              <iframe
                src={REPLAY_VIDEO_URL}
                title="VA Better-Buy Masterclass Replay"
                className="absolute inset-0 w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div
              className="relative aspect-video border border-white/12 bg-[#00224F] flex flex-col items-center justify-center gap-5 px-8 text-center"
              data-testid="replay-video-placeholder"
            >
              <span className="reg-mark top-3 left-3 border-t-2 border-l-2" />
              <span className="reg-mark top-3 right-3 border-t-2 border-r-2" />
              <span className="reg-mark bottom-3 left-3 border-b-2 border-l-2" />
              <span className="reg-mark bottom-3 right-3 border-b-2 border-r-2" />
              <div className="h-20 w-20 rounded-full border-2 border-[#0162CA] flex items-center justify-center pulse-accent">
                <span className="text-[#0162CA] text-2xl ml-1">▶</span>
              </div>
              <NC label="WEBINAR REPLAY VIDEO URL" />
              <p className="font-mono-strict text-[10px] tracking-[0.2em] text-white/30">
                FULL MASTERCLASS RECORDING · HOSTED PLAYER GOES HERE
              </p>
            </div>
          )}
          <button
            type="button"
            data-cursor="cta"
            data-testid="replay-hero-cta"
            onClick={() => scrollToId("apply")}
            className="cta-btn w-full mt-4"
          >
            {APPLY_CTA_LABEL}
          </button>
          <p className="mt-3 text-[10px] text-white/25 text-center">
            Free review · No fee to you · Nothing here is an approval or a commitment to lend
          </p>
        </motion.div>
      </div>
    </section>
  );
}
